/**
 * bizClient — thin wrapper around apiClient for `/api/biz/*` routes.
 *
 * Differences from the default apiClient:
 *   • baseURL is `APP_ENV.bizApiBaseUrl` (default `/api`) instead of `/api/v1`.
 *     The Business namespace lives outside the legacy customer surface so
 *     tenant resolution and RLS happen via `business-tenant-context`
 *     preHandler, not the consumer JWT pipeline.
 *   • Sends `x-business-branch-id` (kebab, RFC 7230) when a local active
 *     branch is stored. `createBusinessTenantContextPreHandler` on the
 *     backend reads this header to disambiguate multi-branch actors
 *     (ADR-101 Layer 2).
 *   • Inherits `Authorization: Bearer` + `X-Client` headers from apiClient
 *     request interceptor — no need to re-implement auth plumbing.
 *
 * Usage:
 *   const snapshot = await bizGet<InvoiceListSnapshot>('/biz/invoices', { params });
 *   const invoice  = await bizPost<InvoiceRow>('/biz/invoices/:id/issue', body);
 *
 * Rationale for NOT creating a second Axios instance: duplication of
 * interceptors + the fact that every Business page also consumes `/api/v1`
 * endpoints (login, shared lookups) made a single shared instance with
 * per-call baseURL override the pragmatic choice.
 *
 * @see APP_ENV.bizApiBaseUrl / APP_ENV.bizApiMode (shared/config/env.ts)
 * @see backend/src/middleware/business-tenant-context.ts (header contract)
 */
import type { AxiosRequestConfig } from 'axios';

import {
  apiClient,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  getActiveBranchId,
} from './apiClient';
import { APP_ENV } from '../config/env';

const BRANCH_HEADER = 'x-business-branch-id';

function mergeConfig(config?: AxiosRequestConfig): AxiosRequestConfig {
  const merged: AxiosRequestConfig = {
    ...config,
    baseURL: APP_ENV.bizApiBaseUrl,
  };
  const branchId = getActiveBranchId();
  if (branchId) {
    merged.headers = {
      ...(config?.headers as Record<string, string> | undefined),
      [BRANCH_HEADER]: branchId,
    };
  }
  return merged;
}

export async function bizGet<T>(
  path: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  return apiGet<T>(path, mergeConfig(config));
}

export async function bizPost<T, B = unknown>(
  path: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  return apiPost<T, B>(path, body, mergeConfig(config));
}

export async function bizPatch<T, B = unknown>(
  path: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  return apiPatch<T, B>(path, body, mergeConfig(config));
}

export async function bizDelete<T>(
  path: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  return apiDelete<T>(path, mergeConfig(config));
}

/**
 * Escape-hatch re-export for callers that need the raw instance (e.g.
 * streaming downloads where apiGet's JSON typing is unhelpful). Prefer the
 * typed helpers above wherever possible.
 */
export { apiClient as bizClient };
